import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { environment } from '@env/environment';
import { Comment, ProductsService, Product, Negotiation, ProductReservation, User } from '@vestiaire/api-sdk-angular';
import * as dayjs from 'dayjs';
import { MmaoService } from '@app/shared/components/mmao/services/mmao.service';
import { Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import { Subscription } from 'rxjs';

@Component({
  selector: 'vc-message',
  templateUrl: './message.component.html',
  styleUrls: ['./message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessageComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  message: Comment;

  @Input()
  isModal = false;

  @Input()
  translateAll: boolean;

  @Input()
  likedItem = [];

  @Input()
  product: Product;

  @Input()
  negotiation: Negotiation | null;

  @Input()
  reservation: ProductReservation;

  @Input()
  user: User;

  /**
   * If we are on an Edito page we pass the legacy Edito ID
   * Default to zero because use only to translate a comment
   *
   * @type {string}
   * @memberof DiscussionsComponent
   */
  @Input()
  public editoId = '0';

  /**
   * Boolean to show the like functionality
   *
   * @type {boolean}
   * @memberof MessageComponent
   */
  @Input()
  public showLikeFunctionality: boolean;

  public imgixPath = environment.imgixPath;
  public isSeller = false;
  public isTranslated = false;
  public translatedMsg: string;
  public dateFormat: {
    date: string;
    hours: string;
  };
  private storeSubscription: Subscription;

  constructor(
    private productsService: ProductsService,
    private mmaoService: MmaoService,
    private store: Store<IAppState>
  ) {}

  ngOnInit() {
    if (this.product && this.product.seller.id === this.message.author.id) {
      this.isSeller = true;
    }
    this.dateFormat = this.formatDate(new Date(this.message.date));

    this.storeSubscription = this.store.select('mmao').subscribe(
      res => {
        if (this.isConcernedNegotiation(res)) {
          this.negotiation = res.negotiation;
        }
      },
      err => console.log(err)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.translateAll) {
      if (this.translateAll) {
        this.translate();
      } else {
        this.untranslate();
      }
    }
  }

  ngOnDestroy() {
    this.storeSubscription.unsubscribe();
  }

  isConcernedNegotiation(res) {
    return res && res.negotiation && res.negotiation.id && res.commentId && res.commentId === this.message.id;
  }

  translate() {
    // This Id is useless because the legacy method `textTranslate` only work with the
    // comments ID not with a product ID on reference
    // You can also have a look on Atlas `TranslationController` method `translate`
    const uselessId = this.product && this.product.id ? this.product.id : this.editoId;

    if (!this.translatedMsg && this.message.isTranslatable) {
      this.productsService.translation(this.message.id, uselessId).subscribe(
        res => {
          if (res.data) {
            this.translatedMsg = res.data.translation;
            this.isTranslated = true;
          }
        },
        err => {
          // Si err : le msg n'est pas translatable
          this.message.isTranslatable = false;
        }
      );
    } else {
      this.isTranslated = true;
    }
  }

  untranslate() {
    this.isTranslated = false;
  }

  formatDate(date: Date) {
    return {
      date: dayjs(date).format('DD.MM.YY'),
      hours: dayjs(date).format('HH:mm')
    };
  }

  get isLiked(): boolean {
    if (!Array.isArray(this.likedItem)) {
      return false;
    }
    return this.likedItem.includes(String(this.message.id));
  }

  /**
   * Seller can make offer if:
   *   - user is seller
   *   - comment author is not the seller
   *   - comment is about price: isNegotiationSellerOffer
   *   - MMAO canMakeAnOffer conditions
   */
  get sellerCanMakeAnOffer(): boolean {
    if (this.user && this.product) {
      return (
        this.user.id === this.product.seller.id &&
        this.user.id !== this.message.author.id &&
        this.message.isNegotiationSellerOffer &&
        this.mmaoService.sellerCanMakeAnOffer(this.product, this.reservation)
      );
    }

    return false;
  }
}
